import React, { useState } from 'react';
import {
  Box,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Typography,
  CircularProgress,
} from '@mui/material';
import { toast } from 'react-toastify';
import {
  PLAN_STATUS_OPTIONS,
  StockPlanStatus,
  planStatusIndex,
  planStatusLabel,
} from '../../../../models/StockPickTypes';
import { updateStockPlanStatus } from '../../../../services/request/StockPick';

interface Props {
  planId: string;
  status?: StockPlanStatus;
  onStatusChanged: (status: StockPlanStatus) => void;
}

const PlanStatusStepper: React.FC<Props> = ({ planId, status, onStatusChanged }) => {
  const [busy, setBusy] = useState(false);
  const activeStep = planStatusIndex(status ?? 'CREATED');

  const handleStepClick = async (stepIndex: number) => {
    const next = PLAN_STATUS_OPTIONS[stepIndex]?.value;
    if (!next || next === status || busy) return;
    setBusy(true);
    try {
      await updateStockPlanStatus(planId, next);
      onStatusChanged(next);
      toast.success(`Status updated to ${planStatusLabel(next)}`);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'Failed to update status');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, position: 'relative' }}>
      <Typography variant="subtitle2" color="primary" gutterBottom>
        Plan stage
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
        Click a stage to move this plan through its lifecycle.
      </Typography>
      <Box sx={{ overflowX: 'auto', pb: 0.5 }}>
        <Stepper
          activeStep={activeStep}
          alternativeLabel
          nonLinear
          sx={{ minWidth: 640 }}
        >
          {PLAN_STATUS_OPTIONS.map((stage, index) => (
            <Step key={stage.value} completed={index < activeStep}>
              <StepLabel
                optional={
                  index === activeStep ? (
                    <Typography variant="caption" color="primary">Current</Typography>
                  ) : undefined
                }
                sx={{
                  cursor: busy ? 'default' : 'pointer',
                  '& .MuiStepLabel-label': {
                    fontSize: '0.75rem',
                    lineHeight: 1.2,
                  },
                }}
                onClick={() => handleStepClick(index)}
              >
                {stage.label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>
      {busy && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            bgcolor: 'rgba(255,255,255,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 1,
          }}
        >
          <CircularProgress size={24} />
        </Box>
      )}
    </Paper>
  );
};

export default PlanStatusStepper;
